import { useState, useEffect, useCallback, useRef } from 'react';
import { api, errMsg } from '../api.js';
import { toast } from './Toasts.jsx';
import TodayCard from './TodayCard.jsx';

// Analytics-Roadmap P1 — self-fetching Today card for the Dashboard "Today" zone.
// Pulls the thin /analytics/cadence slice (not the whole funnel) + drives the
// daily-goal stepper, mirroring AnalyticsPage. Renders nothing until loaded so
// it never flashes empty.
export default function DashboardTodayCard() {
  const [cadence, setCadence] = useState(null);
  // T290c — what the SERVER last confirmed, not what is on screen. The first
  // version of the restore captured the current cadence, which is the optimistic
  // one after a click. Two clicks before the first PATCH resolves then made the
  // second capture the unconfirmed 4, so a failure restored a goal the server had
  // never accepted — a different wrong number from the one being fixed.
  const confirmedRef = useRef(null);
  // And a sequence, so only the NEWEST attempt is allowed to restore. Otherwise a
  // slow first request failing after a fast second one succeeded would drag the
  // screen back and silently undo a save that worked.
  const seqRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const load = useCallback(async () => {
    try {
      const tz = -new Date().getTimezoneOffset();
      const d = await api.get(`/analytics/cadence?tzOffset=${tz}`);
      confirmedRef.current = d.cadence;
      if (mountedRef.current) setCadence(d.cadence);
    } catch {
      /* keep last value; the dashboard has plenty of other content */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState !== 'hidden') load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  const setGoal = useCallback(
    async (next) => {
      const clamped = Math.max(0, Math.min(50, Math.round(next)));
      // T290b — the value to put back if the server refuses. The first version
      // of this fix toasted and left the optimistic number on screen, trusting
      // the load() below to reconcile it. On a dead network that refetch fails
      // too and its catch keeps the last value, so the card sat showing a goal
      // of 4 while /preferences held 3 — permanently, and after five seconds
      // the toast was gone and nothing on screen said so. The title of the test
      // covering it promised exactly the guarantee the code did not give.
      const previous = confirmedRef.current;
      seqRef.current += 1;
      const mySeq = seqRef.current;
      setCadence((c) =>
        c
          ? {
              ...c,
              goal: clamped || null,
              goalMetToday: clamped ? c.appliedToday >= clamped : false,
              daysHitGoalThisWeek: clamped ? c.byDay.filter((d) => d.count >= clamped).length : 0,
            }
          : c,
      );
      try {
        await api.patch('/preferences', { dailyApplicationGoal: clamped || null });
      } catch (e) {
        // Put the old number back rather than leaving one the server rejected,
        // and name the action in the message: errMsg prefers e.message, so the
        // fallback never fired and a dropped connection toasted the bare
        // "Failed to fetch" with no hint of what had failed.
        if (mySeq !== seqRef.current) return; // a newer attempt supersedes this one
        if (previous) setCadence(previous);
        toast(`Could not save your daily goal — ${errMsg(e, 'the server did not respond')}`, {
          kind: 'error',
        });
        return;
      } finally {
        load();
      }
    },
    [load],
  );

  if (!cadence) return null;
  return <TodayCard cadence={cadence} onSetGoal={setGoal} />;
}
